import React from 'react';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import ProjectIcon from '@atlaskit/icon/glyph/people-group';
import CommitIcon from '@atlaskit/icon-object/glyph/commit/16';
import PullRequestIcon from '@atlaskit/icon-object/glyph/pull-request/16';
import BranchIcon from '@atlaskit/icon-object/glyph/branch/16';
import RepoIcon from '@atlaskit/icon-object/glyph/code/16';
import TaskIcon from '@atlaskit/icon-object/glyph/task/16';
import { extractorPriorityMap } from './priority';
import { extractProvider, extractUrlFromIconJsonLd, extractTitle } from '@atlaskit/link-extractors';
import { extractFileFormat } from './extractFileFormat';
import { extractIconFromDocument } from './extractIconFromDocument';
import { extractIconFromTask } from './extractIconFromTask';
import { extractTaskType } from '../lozenge/extractTaskType';
import { prioritiseIcon } from './prioritiseIcon';
export const extractIcon = (jsonLd, priority = 'type') => {
  const type = jsonLd['@type'];
  const opts = {
    title: extractTitle(jsonLd),
    provider: extractProvider(jsonLd),
    fileFormat: extractFileFormat(jsonLd),
    taskType: extractTaskType(jsonLd),
    icon: jsonLd.icon && extractUrlFromIconJsonLd(jsonLd.icon),
    priority
  };
  if (Array.isArray(type)) {
    const highestPriorityType = type.sort((a, b) => extractorPriorityMap[b] - extractorPriorityMap[a])[0];
    return extractIconByType(highestPriorityType, opts);
  } else {
    return extractIconByType(type, opts);
  }
};

/**
 * Extracts an icon based on the given type.
 *
 * Some types return hardcoded icons, while others follow a priority list on how to pick the icon.
 * If type is not recognized, it will return the icon from the provider, or `undefined` if no icon is found.
 *
 * @param type - The type of the object.
 * @param opts - Options for the icon extraction.
 * @returns The extracted icon as a React node, or `undefined` if no icon is found.
 */
const extractIconByType = (type, opts) => {
  switch (type) {
    case 'Document':
    case 'schema:BlogPosting':
    case 'schema:DigitalDocument':
    case 'schema:TextDigitalDocument':
    case 'schema:PresentationDigitalDocument':
    case 'schema:SpreadsheetDigitalDocument':
    case 'atlassian:Template':
    case 'atlassian:UndefinedLink':
      return extractIconFromDocument(type, opts);
    case 'atlassian:Goal':
      return opts.icon || /*#__PURE__*/React.createElement(TaskIcon, {
        label: opts.title || 'goal',
        testId: "task-icon"
      });
    case 'atlassian:Project':
      return opts.icon || /*#__PURE__*/React.createElement(ProjectIcon, {
        label: opts.title || 'project',
        size: "small",
        testId: "project-icon"
      });
    case 'atlassian:SourceCodeCommit':
      return /*#__PURE__*/React.createElement(CommitIcon, {
        label: opts.title || 'commit',
        testId: "commit-icon"
      });
    case 'atlassian:SourceCodePullRequest':
      return /*#__PURE__*/React.createElement(PullRequestIcon, {
        label: opts.title || 'pullRequest',
        testId: "pull-request-icon"
      });
    case 'atlassian:SourceCodeReference':
      return /*#__PURE__*/React.createElement(BranchIcon, {
        label: opts.title || 'reference',
        testId: "branch-icon"
      });
    case 'atlassian:SourceCodeRepository':
      return /*#__PURE__*/React.createElement(RepoIcon, {
        label: opts.title || 'repository',
        testId: "repo-icon"
      });
    case 'atlassian:Task':
      return extractIconFromTask(opts);
    default:
      return getBooleanFF('platform.linking-platform.smart-card.standardise-smart-link-icon-behaviour') ? extractDefaultIcon(opts) : opts.provider && opts.provider.icon;
  }
};

/**
 * Choose which icon to show when the type is not recognized.
 */
const extractDefaultIcon = opts => {
  var _opts$provider;
  const {
    provider
  } = opts;
  const providerId = provider === null || provider === void 0 ? void 0 : provider.id;
  return prioritiseIcon({
    providerId,
    fileFormatIcon: undefined,
    documentTypeIcon: undefined,
    urlIcon: opts.icon,
    providerIcon: (_opts$provider = opts.provider) === null || _opts$provider === void 0 ? void 0 : _opts$provider.icon
  });
};